import {
  Group,
  Mesh,
  type Material,
  type MeshLambertMaterial,
  type Scene
} from "three"
import { createSharedEntityXYGridMapMemo } from "../../../rx/memo/createSharedEntityXYGridMapMemo"
import { createSharedTileGraphNodeCollectionMemo } from "../../../rx/memo/createSharedTileGraphNodeCollectionMemo"
import { useHoveredPositionSignal } from "../../../rx/signal/useHoveredPositionSignal"
import { usePaintTileOptionSignal } from "../../../rx/signal/usePaintTileOptionSignal"
import { useSelectedPlayerIdSignal } from "../../../rx/signal/useSelectedPlayerIdSignal"
import { useSelectedToolSignal } from "../../../rx/signal/useSelectedToolSignal"
import type { Engine } from "../../core/engine/Engine.type"
import createDummyEntityGraphNode from "../../core/engine/EntityGraphNode/createDummyEntityGraphNode"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import getPlayerGraphNode from "../../core/engine/PlayerGraphNode/getPlayerGraphNode"
import type { EntityId } from "../../core/entity/EntityId.type"
import getEntityRenderHeight from "../../core/entity/getEntityRenderHeight"
import findByPosition from "../../core/findByPosition"
import { SelectedToolId } from "../../core/map_editor/SelectedToolId.enum"
import type { PlayerIdOrNull } from "../../core/player/PlayerIdOrNull"
import tmpRng from "../../core/rng/tmpRng"
import type { EntityList } from "../../core/state/EntityList.type"
import toCoord from "../../core/tile_position_xy/toCoord"
import { isArray } from "../../core/util/Array"
import { byLayerIdDesc } from "../../core/util/id"
import { sin } from "../../core/util/math/Math"
import { deepClone } from "../../deep_clone"
import { mountOnFrameUpdateToObject3D } from "../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import Unexpected from "../../Exception/Unexpected.class"
import type { EntityThreeModel } from "../EntityThreeModel/EntityThreeModel.type"
import createEntityThreeModelCollectionV2 from "./createEntityThreeModelCollectionV2"
import generateNoiseRGBTexture from "./generateNoiseRGBTexture"

export type EntityThreeModelCollection = {
  group: Group
  ents: Map<EntityId, EntityThreeModel>
}

export default function createPaintedTileOptionEntityThreeModelCollection(
  engine: Engine,
  scene: Scene,
): void {
  const [getSelectedTool] = useSelectedToolSignal(engine)
  const [getHoveredPosition] = useHoveredPositionSignal(engine)
  // const glimmerAoMap = createGlimmerAoMapTexture()
  // const glimmerAoMap = generateNoiseTexture(128, tmpRng, 0, 255)
  const glimmerAoMap = generateNoiseRGBTexture(128, tmpRng, 0, 64)

  const getTileNodeList = createSharedTileGraphNodeCollectionMemo(engine)

  const getEntityListAtXYMap = createSharedEntityXYGridMapMemo(engine)
  let id = 0
  let rh = -1
  const [getPaintTileOption] = usePaintTileOptionSignal(engine)
  const [getSelectedPlayerId] = useSelectedPlayerIdSignal(engine)

  function getEntitySourceList(): Array<EntityGraphNode> {
    const selectedTool = getSelectedTool()
    if (selectedTool === SelectedToolId.Paint) {
      const { entity: ent0 } = getPaintTileOption()
      if (ent0) {
        const entity = deepClone(ent0)
        if ('player_id' in entity) {
          entity.player_id = getSelectedPlayerId()
        }
        const hoveredPosition = getHoveredPosition()
        if (hoveredPosition) {
          const stack0: EntityList = getEntityListAtXYMap().get(toCoord(hoveredPosition)) || []
          const stack1: EntityList = stack0.filter((ent) => ent.layer_id !== entity.layer_id)
          stack1.push(entity)
          stack1.sort(byLayerIdDesc)
          rh = getEntityRenderHeight(stack1, entity)
          // these IDs won't interact with the real state, so we can just increment
          id++
          const tileNode = findByPosition(getTileNodeList(), hoveredPosition)
          if (!tileNode) {
            throw new Unexpected('!tileNode')
          }
          const playerNode = getPlayerGraphNode(engine, entity.player_id as PlayerIdOrNull)
          const ent1 = {
            ...entity,
            ...hoveredPosition,
            id,
          }
          const entNode: EntityGraphNode = createDummyEntityGraphNode(ent1, tileNode, playerNode)
          return [
            // ...engine.state.ents.filter((otherEnt) => {
            //   return !(otherEnt.layer_id === entity.layer_id && samePosition(otherEnt, hoveredPosition))
            // }),
            entNode,
          ]
        }
      }
    }
    rh = -1
    return []
  }

  const paintTilePreviewEntityThreeModelCollection = createEntityThreeModelCollectionV2(engine, scene, getEntitySourceList, 'PaintTilePreviewEnts')
  const { group } = paintTilePreviewEntityThreeModelCollection

  const glimmered = new Set<Material>()

  function onMaterialDisposed(this: Material) {
    // console.log('onMaterialDisposed', this)
    glimmered.delete(this)
  }

  function glimerify(material: Material) {
    if (!glimmered.has(material)) {
      glimmered.add(material)
      if ('aoMap' in material && !material.aoMap) {
        // console.log('glimmering', material)
        // IMPORTANT: For aoMap to work correctly, the geometries of the meshes
        // must have a second set of UV coordinates (uv2). If not present,
        // the aoMap will not be applied as expected.
        // Ensure that models created by createEntityThreeModelCollectionV2 have uv2 attributes.
        material.aoMap = glimmerAoMap
          ; (material as MeshLambertMaterial).aoMapIntensity = 1.0 // Default is 1.0, can be adjusted
        material.userData.glimmerAoMapApplied = true // Flag to avoid re-applying
        material.needsUpdate = true // Inform Three.js that the material has changed

        material.addEventListener('dispose', onMaterialDisposed)
      }
    }
  }

  // Integrate AO map application into the group's update loop
  // This ensures that dynamically added/changed meshes also get the AO map.
  mountOnFrameUpdateToObject3D(engine, group, (deltaMs: number, frameAt: number) => {
    // Existing animation logic for group position
    const s1 = sin(frameAt / 500)
    group.position.y = rh + (2 + (s1 * s1) / 2) // 'rh' is captured from the outer scope and updated by the callback above

    // Apply glimmer AO map to relevant meshes
    group.traverse((object) => {
      if (object instanceof Mesh) {
        const mesh = object as Mesh
        const { material } = mesh
        if (isArray(material)) {
          material.forEach(glimerify)
        } else {
          glimerify(material)
        }
      }
    })

    glimmered.forEach((material) => {

      // const s1 = sin(frameAt / 500)
      // group.position.y = rh + 1.001 + (1 + (s1 * s1) / 2) // 'rh' is captured from the outer scope and updated by the callback above

      ; (material as MeshLambertMaterial).aoMapIntensity = s1 / 2
      // not required for updating aoMapIntensity, already updated in glimerify
      // material.needsUpdate = true
    })
  })

  // setTimeout(() => {
  //   engine.selectedTool = SelectedToolId.Paint
  //   setPaintToolOption(paintTileOptionList[2])
  // }) // This will likely trigger entity/mesh changes, testing the dynamic application
}
